import type { Body, Scene } from "../../types/scene";
import { logger } from "../../utils/logger";

/**
 * Type for bodies with reconstructed children (full Body objects instead of UUIDs).
 * In the actual scene data, `Body.children` are `BodyUUID[]` (strings).
 *
 * Prefer flat `Record<BodyUUID, Body>` access for direct lookups. Use this type only
 * when hierarchy is genuinely needed from `getBodyTree()`.
 */
export type BodyWithChildren = Omit<Body, "children"> & { children: BodyWithChildren[] };

type BodyChildObject = { uuid?: string; children?: BodyChildRef[] };
type BodyChildRef = string | BodyChildObject;
type BodyRecordBody = { uuid?: string; name?: string; children?: BodyChildRef[] };
type BodyRecordLike = { [uuid: string]: BodyRecordBody | undefined };
type WorldBodyInput = string | { uuid?: string; rootBodyUuid?: string; name?: string };
type EffectiveWorldBody = { uuid: string; rootBodyUuid?: string; name?: string };
type BodyTreeState = {
  bodies?: Scene["bodies"] | BodyRecordLike | BodyWithChildren[];
  worldBodies?: Scene["worldBodies"] | WorldBodyInput[];
};

/**
 * Helper to reconstruct the body tree from the flat map.
 * This reconstructs the tree structure from the flat map + worldBodies array.
 *
 * This function is expensive (O(n) traversal + reconstruction).
 * Prefer direct map access (`state.bodies[uuid]`) for O(1) lookups; use `getBodyTree`
 * for UI hierarchy, structural operations, and serialization that genuinely need a tree.
 */
const isBodyObject = (value: unknown): value is BodyChildObject =>
  !!value && typeof value === "object" && "uuid" in value;

const getWorldBodyUuid = (worldBody: WorldBodyInput): string | undefined =>
  typeof worldBody === "string" ? worldBody : worldBody.uuid;

const toEffectiveWorldBody = (worldBody: WorldBodyInput): EffectiveWorldBody | null => {
  if (typeof worldBody === "string") {
    return { uuid: worldBody };
  }
  if (typeof worldBody.uuid !== "string") {
    return null;
  }
  return {
    uuid: worldBody.uuid,
    ...(typeof worldBody.rootBodyUuid === "string" ? { rootBodyUuid: worldBody.rootBodyUuid } : {}),
    ...(typeof worldBody.name === "string" ? { name: worldBody.name } : {}),
  };
};

export const getBodyTree = (state: BodyTreeState): BodyWithChildren[] => {
  const bodies = state.bodies;
  const worldBodies: WorldBodyInput[] = state.worldBodies || [];

  if (Array.isArray(bodies)) {
    // Legacy: already an array
    return bodies;
  }

  const bodiesMap: BodyRecordLike | undefined = bodies;

  if (!bodiesMap || Object.keys(bodiesMap).length === 0) {
    return [];
  }

  // Track visited UUIDs to prevent infinite loops from circular references
  const visitedInCurrentPath = new Set<string>();

  const reconstructTree = (uuid: string): BodyWithChildren | null => {
    // Circular reference detection: if we've already seen this UUID in the current path, skip it
    if (visitedInCurrentPath.has(uuid)) {
      logger.warn(
        `[getBodyTree] Circular reference detected for UUID "${uuid}", skipping to prevent infinite loop`
      );
      return null;
    }

    const body = bodiesMap[uuid];
    if (!body) {
      logger.warn(
        `[getBodyTree] Body with UUID "${uuid}" not found in bodiesMap. Available keys:`,
        Object.keys(bodiesMap).slice(0, 10)
      );
      return null;
    }

    // Mark as visiting
    visitedInCurrentPath.add(uuid);

    const reconstructedChildren = (body.children || [])
      .map((child) => {
        if (typeof child === "string") {
          const reconstructed = reconstructTree(child);
          if (!reconstructed) {
            // Only warn if it's not a circular reference (already warned above)
            if (!visitedInCurrentPath.has(child) || child === uuid) {
              logger.warn(
                `[getBodyTree] Failed to reconstruct child "${child}" of body "${body.name}"`
              );
            }
          }
          return reconstructed;
        }
        return isBodyObject(child) ? (child as BodyWithChildren) : null;
      })
      .filter((c): c is BodyWithChildren => c !== null);

    // Unmark after processing (allow same node in different branches)
    visitedInCurrentPath.delete(uuid);

    return {
      ...body,
      children: reconstructedChildren,
    } as BodyWithChildren;
  };

  // Handle both WorldBody[] and legacy string[] formats
  const worldBodyUuids = worldBodies.map(getWorldBodyUuid);

  return worldBodyUuids
    .filter((uuid): uuid is string => typeof uuid === "string")
    .map(reconstructTree)
    .filter((b): b is BodyWithChildren => b !== null);
};

/**
 * Build a robust root-body list from `bodies` + `worldBodies`.
 *
 * `worldBodies` is the canonical root list, but it can temporarily drift out of sync
 * with the actual parent/child graph. This helper keeps all consumers (viewer + panels)
 * aligned by falling back to computed roots when the declared list is clearly invalid.
 */
export const getEffectiveWorldBodies = (state: BodyTreeState): EffectiveWorldBody[] => {
  const bodiesMap =
    state?.bodies && typeof state.bodies === "object" && !Array.isArray(state.bodies)
      ? (state.bodies as BodyRecordLike)
      : {};
  const worldList: WorldBodyInput[] = Array.isArray(state?.worldBodies) ? state.worldBodies : [];

  const bodyKeys = new Set(Object.keys(bodiesMap));
  if (bodyKeys.size === 0) return worldList.map(toEffectiveWorldBody).filter((wb) => wb !== null);

  const childKeys = new Set<string>();
  Object.values(bodiesMap).forEach((b) => {
    if (!b) return;
    const kids = Array.isArray(b?.children) ? b.children : [];
    kids.forEach((c) => {
      if (typeof c === "string") {
        if (bodyKeys.has(c)) childKeys.add(c);
        return;
      }
      if (isBodyObject(c) && typeof c.uuid === "string") {
        if (bodyKeys.has(c.uuid)) childKeys.add(c.uuid);
      }
    });
  });

  const computedRoots = Array.from(bodyKeys).filter((k) => !childKeys.has(k));

  const declaredRootUuids = worldList
    .map(getWorldBodyUuid)
    .filter((u): u is string => typeof u === "string" && bodyKeys.has(u));
  const declaredRootSet = new Set(declaredRootUuids);

  const declaredCoversAllRoots =
    computedRoots.length === 0 ? true : computedRoots.every((r) => declaredRootSet.has(r));
  const declaredLooksBloated =
    computedRoots.length > 0 && declaredRootSet.size > Math.max(3, computedRoots.length * 3);

  if (declaredCoversAllRoots && !declaredLooksBloated) {
    return worldList.map(toEffectiveWorldBody).filter((wb) => wb !== null);
  }

  const nameByUuid = new Map<string, string>();
  worldList.forEach((wb) => {
    if (
      wb &&
      typeof wb === "object" &&
      typeof wb.uuid === "string" &&
      typeof wb.name === "string"
    ) {
      nameByUuid.set(wb.uuid, wb.name);
    }
  });

  return computedRoots.map((uuid) => ({ uuid, name: nameByUuid.get(uuid) || uuid }));
};
